'use client';
import React, { useEffect, useRef, useState } from 'react';

interface Project {
  id: number;
  name: string;
  description: string;
  image: string;
  alt: string;
}

const projects: Project[] = [
{
  id: 1,
  name: 'Commercial Project',
  description: 'Automated stack parking system installed at a commercial complex. Completed Q1 2024.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fddb6db1-1786003972537.png",
  alt: 'Bhargav Parking completed commercial project — automated parking system installed at a commercial building'
},
{
  id: 2,
  name: 'Hospital Project',
  description: 'Horizontal circulation parking system for a hospital complex. Completed Q2 2024.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1644ff5d5-1785956177057.png",
  alt: 'Bhargav Parking hospital project — automated parking system installed at a hospital complex'
},
{
  id: 3,
  name: 'Tower Parking',
  description: 'Multi-level tower parking installation at a residential high-rise. Completed Q3 2024.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5185c57-1786003972091.png",
  alt: 'Bhargav Parking project installation — completed automated parking system at a multi-storey building'
},
{
  id: 4,
  name: 'Precision Engineering',
  description: 'Custom precision-engineered parking components for a mixed-use development.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e4fdd32e-1772792738039.png",
  alt: 'Bhargav Parking close-up detail of precision-engineered parking system mechanical components'
},
{
  id: 5,
  name: 'Factory Production',
  description: 'In-house manufacturing of automated parking steel structures at Daman facility.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_116716d11-1786003973307.png",
  alt: 'Bhargav Parking factory floor — in-house manufacturing of automated parking system steel structures'
},
{
  id: 6,
  name: 'Welding & Fabrication',
  description: 'Steel fabrication and welding for large-scale parking system frames at Daman.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15036084b-1772392663907.png",
  alt: 'Bhargav Parking welding and fabrication — skilled workers manufacturing parking system steel frames'
}];

export default function ProjectsSection() {
  const [visible, setVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  const ref = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const CARD_WIDTH = 260;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const handlePrev = () => {
    if (scrollRef.current) {
      const card = scrollRef.current.children[0] as HTMLElement;
      if (card) {
        const gap = parseInt(window.getComputedStyle(scrollRef.current).gap) || 20;
        scrollRef.current.scrollLeft -= (card.offsetWidth + gap);
      }
    }
  };

  const handleNext = () => {
    if (scrollRef.current) {
      const card = scrollRef.current.children[0] as HTMLElement;
      if (card) {
        const gap = parseInt(window.getComputedStyle(scrollRef.current).gap) || 20;
        scrollRef.current.scrollLeft += (card.offsetWidth + gap);
      }
    }
  };

  return (
    <section
      id="projects"
      className="py-20 bg-[#f5f7fa]"
      style={{ fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}>
      
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 lg:items-start">

          {/* LEFT: Title + Description + Arrows */}
          <div
            className={`flex-shrink-0 lg:w-[240px] flex flex-col justify-between transition-all duration-700 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
            style={{ minHeight: '340px' }}>
            
            <div>
              <p className="text-[#00AAE2] text-[13px] font-semibold tracking-[2.5px] uppercase mb-4">Our Work</p>
              <h2
                className="text-[#062543] font-bold mb-5"
                style={{ fontSize: '36px', lineHeight: '44px', fontFamily: 'Ubuntu, sans-serif' }}>
                Featured<br />Projects
              </h2>
              <p
                className="text-[#6b7a8d]"
                style={{ fontSize: '15px', lineHeight: '25px', fontFamily: 'Ubuntu, sans-serif' }}>
                We have completed 40+ projects across India, delivering precision-engineered parking solutions for diverse clients.
              </p>
            </div>

            {/* Prev / Next arrows */}
            <div className="flex items-center gap-3 mt-10">
              <button
                onClick={handlePrev}
                disabled={!canScrollLeft}
                className="w-10 h-10 flex items-center justify-center border border-[#c5cdd8] rounded-sm bg-white hover:bg-[#00AAE2] hover:border-[#00AAE2] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed text-[#062543]"
                aria-label="Previous project">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={handleNext}
                disabled={!canScrollRight}
                className="w-10 h-10 flex items-center justify-center border border-[#c5cdd8] rounded-sm bg-white hover:bg-[#00AAE2] hover:border-[#00AAE2] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed text-[#062543]"
                aria-label="Next project">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>

          {/* RIGHT: Scrollable project cards */}
          <div className="flex-1 w-full min-w-0 overflow-hidden">
            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className={`flex gap-5 overflow-x-auto scroll-smooth transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: '4px',
              }}>
              {projects.map((project, i) => (
                <div
                  key={project.id}
                  className="flex-shrink-0 bg-white group cursor-pointer"
                  style={{
                    width: `${CARD_WIDTH}px`,
                    minWidth: `${CARD_WIDTH}px`,
                    transitionDelay: `${i * 60}ms`,
                    boxShadow: '0 2px 12px rgba(6,25,46,0.07)',
                  }}>
                  
                  {/* Tall image */}
                  <div className="overflow-hidden" style={{ height: '220px' }}>
                    <img
                      src={project.image}
                      alt={project.alt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>

                  {/* Text below */}
                  <div className="p-4">
                    <h3
                      className="text-[#062543] font-semibold mb-2"
                      style={{ fontSize: '16px', lineHeight: '22px', fontFamily: 'Ubuntu, sans-serif' }}>
                      {project.name}
                    </h3>
                    <p
                      className="text-[#8a96a3] mb-3"
                      style={{ fontSize: '13px', lineHeight: '20px', fontFamily: 'Ubuntu, sans-serif' }}>
                      {project.description}
                    </p>
                    <span
                      className="text-[#00AAE2] text-[12px] font-semibold tracking-wide uppercase flex items-center gap-1 group-hover:gap-2 transition-all duration-200"
                      style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                      LES MER
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
